/**
 * Meta Pixel — client-side helper.
 *
 * The base pixel + initial PageView fire from src/app/layout.tsx in <head>,
 * before any React component mounts. This module is the thin wrapper every
 * client component imports to push standard / custom events afterwards.
 *
 * Design choices:
 *  - SSR-safe: every call no-ops on the server (typeof window === 'undefined')
 *    so importing this from a 'use client' component that's pre-rendered on
 *    Next.js's server doesn't crash.
 *  - Dedup-friendly: every event takes an optional `eventId`. Pass the SAME
 *    id to the server-side Conversions API and Meta will collapse the two
 *    into one for reporting + attribution. Use `newEventId()` to mint one.
 *  - Standard events first: prefer Meta's standard event names (Purchase,
 *    Lead, Schedule, …) because Aggregated Event Measurement, Advantage+
 *    optimisation, and lookalike sources all index on them.
 */

// fbq's actual signature is variadic and untyped; we only call the two shapes
// the snippet supports: ('track', name, params, {eventID}) and
// ('trackCustom', name, params, {eventID}).
declare global {
  interface Window {
    fbq?: (
      command: 'track' | 'trackCustom' | 'init',
      eventName: string,
      params?: Record<string, unknown>,
      opts?: { eventID?: string }
    ) => void;
  }
}

/** Generate a 32-char hex event_id. Pair this with the matching server CAPI
 *  call so Meta can deduplicate the two records into one. */
export function newEventId(): string {
  // Browser crypto is preferred; falls back to Math.random for the rare
  // user-agent where it's missing. Determinism doesn't matter — uniqueness
  // does, and a 96-bit random space is more than enough.
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return Array.from({ length: 32 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join('');
}

/** True iff we're in a browser and fbq has booted. The init script in
 *  layout.tsx queues calls before the SDK loads, so this technically only
 *  needs to gate on `typeof window` — but checking `fbq` too keeps things
 *  safe for the (rare) case where an ad blocker stripped the script. */
function ready(): boolean {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
}

/** Track a Meta standard event. Returns the eventId used (caller-supplied or
 *  freshly minted) so the server-side mirror can re-use it for dedup. */
export function trackStandardEvent(
  eventName: MetaStandardEvent,
  params?: Record<string, unknown>,
  eventId?: string
): string {
  const id = eventId || newEventId();
  if (!ready()) return id;
  try {
    window.fbq!('track', eventName, params || {}, { eventID: id });
  } catch (err) {
    // Swallow — analytics must never break a user flow.
    console.warn('[metaPixel] track failed:', err);
  }
  return id;
}

/** Track a Meta custom event. Use sparingly — standard events power better
 *  optimisation. Useful for funnel granularity (e.g. "BookingStepReached"). */
export function trackCustomEvent(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string
): string {
  const id = eventId || newEventId();
  if (!ready()) return id;
  try {
    window.fbq!('trackCustom', eventName, params || {}, { eventID: id });
  } catch (err) {
    console.warn('[metaPixel] trackCustom failed:', err);
  }
  return id;
}

/** Meta's documented standard event vocabulary. Limiting the typing surface
 *  to these names keeps every call site self-documenting and lets us avoid
 *  string typos with TS rather than runtime regret. */
export type MetaStandardEvent =
  | 'PageView'
  | 'ViewContent'
  | 'Search'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'InitiateCheckout'
  | 'AddPaymentInfo'
  | 'Purchase'
  | 'Lead'
  | 'CompleteRegistration'
  | 'Contact'
  | 'CustomizeProduct'
  | 'Donate'
  | 'FindLocation'
  | 'Schedule'
  | 'StartTrial'
  | 'SubmitApplication'
  | 'Subscribe';
